import { buildStubBody, escapeJsonForHtml, minifyStubBody } from "@ets/shared";

export interface SnippetOptions {
  cdnUrl?: string;
  async?: boolean;
  autoPage?: boolean;
}

const DEFAULT_CDN_URL = "https://cdn.etus.io/sdk/ets.min.js";

function buildSnippetBody(sdkKey: string, hash: string, options?: SnippetOptions): string {
  const cdnUrl = options?.cdnUrl ?? DEFAULT_CDN_URL;
  const autoPage = options?.autoPage !== false;
  const asyncAttr = options?.async !== false;

  const lines = [
    buildStubBody(sdkKey, hash),
    buildScriptLoader(cdnUrl, asyncAttr),
    ...(autoPage ? ["e.page();"] : []),
  ];

  return lines.join("\n");
}

function buildScriptLoader(cdnUrl: string, async: boolean): string {
  const lines = [
    'var s = document.createElement("script");',
    `s.src = ${escapeJsonForHtml(JSON.stringify(cdnUrl))};`,
    ...(async ? ["s.async = true;"] : []),
    "(document.head || document.documentElement).appendChild(s);",
  ];
  return lines.join("\n");
}

/**
 * Generate a complete `<script>` tag containing the ETS loader snippet.
 *
 * The inline JS captures timing, referrer, and URL, creates a stub `window.ets`
 * object that queues method calls, loads the full SDK bundle asynchronously,
 * and optionally fires an initial pageview.
 */
export function generateSnippet(sdkKey: string, hash: string, options?: SnippetOptions): string {
  const body = buildSnippetBody(sdkKey, hash, options);
  return `<script>\n${body}\n</script>`;
}

/**
 * Same as `generateSnippet` but the inline JS is minified (~550 bytes).
 */
export function generateMinifiedSnippet(
  sdkKey: string,
  hash: string,
  options?: SnippetOptions,
): string {
  const body = buildSnippetBody(sdkKey, hash, options);
  return `<script>${minifyStubBody(body)}</script>`;
}
